import { BaseComponent } from "./base_component"
import { Input, Node, Output, Socket } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { ProjectProperties } from "."
import {
  reconnectLabelOutputsByNormalizedLabel,
  runWithLabelOutputReconnect,
  snapshotLabelOutputConnections,
} from "./categorical_dynamic_io_connections"
import { scheduleDownstreamDynamicLabelOutputRefresh } from "./categorical_label_chain"
import { SelectControl, SelectControlOptions } from "../controls/select"
import { AnnotationRecord } from "../../state/saved_dataset"
import { booleanDataSocket, categoricalDataSocket } from "../socket_types"
import { CategoricalTileGrid } from "../tile_grid"
import { maskFromExtentAndShape } from "../bounding_box"
import { createXYZ } from "ol/tilegrid"
import { GeoJSON } from "ol/format"
import { Feature } from "ol"
import { Geometry } from "ol/geom"


async function renderCategoricalData(projectProps: ProjectProperties, annotation: AnnotationRecord): Promise<CategoricalTileGrid> {
    const tileGrid = createXYZ()
    const mask = await maskFromExtentAndShape(projectProps)
    const outputTileRange = tileGrid.getTileRangeForExtentAndZ(projectProps.extent, projectProps.zoom)
    
    const result = new CategoricalTileGrid(projectProps.zoom, outputTileRange.minX, outputTileRange.minY, outputTileRange.getWidth(), outputTileRange.getHeight())

    // Return empty grid if no GeoJSON URL
    if (!annotation.geojson_url) {
        return result
    }

    try {
        // Fetch the GeoJSON
        const response = await fetch(annotation.geojson_url)
        if (!response.ok) {
            console.warn('Failed to fetch GeoJSON:', response.statusText)
            return result
        }

        const geoJsonData = await response.json()

        // Parse features from GeoJSON
        const geoJsonFormat = new GeoJSON()
        const features = geoJsonFormat.readFeatures(geoJsonData, {
            dataProjection: 'EPSG:3857',
            featureProjection: 'EPSG:3857'
        }) as Feature<Geometry>[]

        // Set properties from GeoJSON properties (like in paintbrush_map.tsx)
        features.forEach(feature => {
            const props = feature.getProperties()
            if (props.classId !== undefined) {
                // Ensure classId is a number
                feature.set("classId", Number(props.classId))
            }
            if (props.order !== undefined && props.order !== null) {
                // Set order directly from GeoJSON properties
                feature.set("order", Number(props.order))
            }
        })

        // Create a map from classId to categorical index
        const classIdToIndex = new Map<number, number>()
        if (annotation.classes) {
            const sortedClasses = [...annotation.classes].sort((a, b) => a.order - b.order)
            sortedClasses.forEach((paintClass, index) => {
                classIdToIndex.set(paintClass.id, index + 1)
            })
        }

        // Create labels map for the categorical grid
        const labels = new Map<number, string>()
        if (annotation.classes) {
            const sortedClasses = [...annotation.classes].sort((a, b) => a.order - b.order)
            sortedClasses.forEach((paintClass, index) => {
                labels.set(index + 1, paintClass.name)
            })
        }
        result.setLabels(labels)

        console.log(features)

        // Sort features by their order property (lower order first, so higher order overwrites)
        const sortedFeatures = features
            .filter(feature => {
                const order = feature.get('order') as number | undefined
                const classId = feature.get('classId') as number | undefined
                return order !== undefined && order !== null && !isNaN(order) && 
                       classId !== undefined && !isNaN(classId) && classIdToIndex.has(classId)
            })
            .sort((a, b) => {
                const orderA = (a.get('order') as number) ?? Infinity
                const orderB = (b.get('order') as number) ?? Infinity
                return orderB - orderA
            })

        console.log(sortedFeatures)

        // Load features into the grid (sorted by order)
        for (const feature of sortedFeatures) {
            const geom = feature.getGeometry()
            if (!geom) continue

            const classId = feature.get('classId') as number | undefined
            if (classId === undefined) continue

            const categoricalIndex = classIdToIndex.get(classId)
            if (categoricalIndex === undefined) continue

            const featureTileRange = tileGrid.getTileRangeForExtentAndZ(
                geom.getExtent(),
                projectProps.zoom
            )

            result.iterateOverTileRange(
                featureTileRange,
                (x, y, value) => {
                    const center = tileGrid.getTileCoordCenter([projectProps.zoom, x, y])
                    if (geom.intersectsCoordinate(center) && mask.get(x, y)) {
                        result.set(x, y, categoricalIndex)
                    }
                }
            )
        }
    } catch (error) {
        console.error('Error loading GeoJSON:', error)
    }

    return result
}

export type getAnnotationsType = () => Promise<AnnotationRecord[]>

export class PaintbrushComponent extends BaseComponent {
    projectProps: ProjectProperties
    paintBrushSelectOptions: SelectControlOptions[]
    annotationSource: getAnnotationsType
    annotations: AnnotationRecord[]

    constructor(getAnnotations: getAnnotationsType, projectProps: ProjectProperties) {
        super("Paintbrush")
        this.category = "Inputs"
        this.projectProps = projectProps
        this.annotationSource = getAnnotations
        this.paintBrushSelectOptions = []
        this.annotations = []
    }

    getPaintBrushSelectOptions(): SelectControlOptions[] {
        return this.paintBrushSelectOptions
    }

    buildOutputs(node: Node) {
        const classOutputKey = (key: string) => key.startsWith("class_")

        const snaps =
            this.editor != null ? snapshotLabelOutputConnections(node, classOutputKey) : []

        // Remove all existing connections and outputs
        node.getConnections().forEach(c => {
            if (c.output.node !== node) {
                this.editor?.removeConnection(c)
            }
        })
        node.getConnections().forEach(c => this.editor?.removeConnection(c))
        Array.from(node.outputs.values()).forEach(output => node.removeOutput(output))

        // Get the selected annotation - handle both string and number IDs
        const selectedIdRaw = node.data.PaintbrushId
        const selectedId = selectedIdRaw !== undefined ? Number(selectedIdRaw) : undefined
        const selectedAnnotation = selectedId !== undefined && !isNaN(selectedId)
            ? this.annotations.find(a => a.id === selectedId)
            : null
        
        // Fallback to first annotation if no valid selection
        const annotation = selectedAnnotation || (this.annotations.length > 0 ? this.annotations[0] : null)

        if (!annotation || !annotation.classes || annotation.classes.length === 0) {
            // No classes available, no outputs
            node.update()
            return
        }


        // Sort classes by order
        const sortedClasses = [...annotation.classes].sort((a, b) => a.order - b.order)
        // Add a Boolean output for each class
        sortedClasses.forEach(paintClass => {
            node.addOutput(new Output(
                `class_${paintClass.id}`,
                paintClass.name,
                booleanDataSocket
            ))
        })

        // Add a Categorical output for "All"
        node.addOutput(new Output("All", "All", categoricalDataSocket))

        if (this.editor) {
            runWithLabelOutputReconnect(node, () =>
                reconnectLabelOutputsByNormalizedLabel(this.editor!, node, snaps, classOutputKey)
            )
        }

        node.update()
        scheduleDownstreamDynamicLabelOutputRefresh(this.editor, node, "All")
    }
    
    async builder(node: Node) {
        this.annotations = await this.annotationSource()
        this.annotations.sort((a, b) => a.name.localeCompare(b.name))
        
        this.paintBrushSelectOptions = this.annotations.map(annotation => ({
            id: annotation.id,
            name: annotation.name
        }))

        // Set default to first annotation if available
        if (this.annotations.length > 0 && node.data.PaintbrushId === undefined) {
            node.data.PaintbrushId = this.annotations[0].id
        }

        // Store node ID for the change callback
        const nodeId = node.id
        const changeHandler = () => {
            // Get the current node from editor to ensure we have latest data
            const currentNode = this.editor?.nodes.find(n => n.id === nodeId)
            if (currentNode) {
                this.buildOutputs(currentNode)
            }
        }

        node.addControl(new SelectControl(
            this.editor,
            'PaintbrushId',
            () => this.paintBrushSelectOptions,
            changeHandler,
            'Paintbrush Project'
        ))

        this.buildOutputs(node)
    }

    async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
        const editorNode = this.editor?.nodes.find(n => n.id === node.id)
        if (editorNode === undefined) { return }

        const paintbrushId = editorNode.data.PaintbrushId as number
        console.log(paintbrushId)
        console.log(this.annotations)
        const paintbrush = this.annotations.find(a => a.id == paintbrushId)
        console.log(paintbrush)
        if (paintbrush === undefined) { return }

        const categoricalData = await renderCategoricalData(this.projectProps, paintbrush)

        paintbrush.classes?.forEach(paintClass => {
            outputs[`class_${paintClass.id}`] = categoricalData.getBoolFromLabel(paintClass.name)
        })

        outputs['All'] = categoricalData

    }
    
}